import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { calculateWorkingDays } from '../../lib/dayCalculation.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.jsx';
import { useUnsavedGuard } from '../../hooks/useUnsavedWork.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import { formatDays } from '@/lib/utils';

export default function NewRequestPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [settings, setSettings] = useState(null);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    typeId: '',
    startDate: '',
    endDate: '',
    reason: '',
  });

  // Snapshot of `form` as first loaded (or as it started, for a brand-new
  // request), used to detect unsaved changes for the unsaved-work guard —
  // this form has no server draft, so the guard + beforeunload prompt IS
  // its only loss-prevention.
  const initialFormRef = useRef(JSON.stringify(form));

  useEffect(() => {
    async function load() {
      try {
        const [typesRes, balRes] = await Promise.all([
          employeeApi.getLeaveTypes(),
          employeeApi.getBalances(),
        ]);
        setLeaveTypes(typesRes.leaveTypes || []);
        setBalances(balRes.balances || {});
        setSettings({ defaultWorkingDays: [0, 1, 2, 3, 4] });

        // Edit mode: load the existing pending request and prefill. Only
        // pending requests are editable — bounce others to their detail page.
        if (editId) {
          const reqRes = await employeeApi.getRequest(editId);
          const r = reqRes.request || {};
          if (r.status !== 'pending' && r.status !== 'rejected') {
            toast.error('Only pending or rejected leave requests can be edited.');
            navigate(`/my-requests/${editId}`);
            return;
          }
          const loaded = {
            typeId: r.typeId || '',
            startDate: r.startDate || '',
            endDate: r.endDate || '',
            reason: r.reason || '',
          };
          setForm(loaded);
          initialFormRef.current = JSON.stringify(loaded);
        } else if (typesRes.leaveTypes?.length > 0) {
          setForm((f) => {
            const seeded = { ...f, typeId: typesRes.leaveTypes[0].rowKey };
            initialFormRef.current = JSON.stringify(seeded);
            return seeded;
          });
        }
      } catch (err) {
        toast.error(`Failed to load: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [editId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = !loading && JSON.stringify(form) !== initialFormRef.current;
  useUnsavedGuard({
    id: `leave-request-${editId || 'new'}`,
    label: `Leave request: ${form.reason || form.typeId || 'new'}`,
    href: editId ? `/request?edit=${editId}` : '/request',
    dirty: isDirty,
  });

  useEffect(() => {
    if (!form.startDate) return;
    const year = form.startDate.substring(0, 4);
    employeeApi.getHolidays(year).then((res) => {
      setHolidays(res.holidays || []);
    }).catch(() => {});
  }, [form.startDate]);

  const selectedType = useMemo(
    () => leaveTypes.find((t) => t.rowKey === form.typeId),
    [leaveTypes, form.typeId]
  );

  const policy = selectedType?.policy || {};

  const workingDays = useMemo(() => {
    if (user?.workingDays) {
      try {
        const parsed = typeof user.workingDays === 'string' ? JSON.parse(user.workingDays) : user.workingDays;
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch { /* fall through */ }
    }
    return settings?.defaultWorkingDays || [0, 1, 2, 3, 4];
  }, [user, settings]);

  const calculatedDays = useMemo(() => {
    if (!form.startDate || !form.endDate) return null;
    if (form.startDate > form.endDate) return 0;
    return calculateWorkingDays(form.startDate, form.endDate, workingDays, holidays, false);
  }, [form.startDate, form.endDate, workingDays, holidays]);

  const balance = balances[form.typeId] || null;
  const remaining = balance ? balance.remaining : (selectedType?.defaultAllowance || 0);
  const insufficientBalance = calculatedDays !== null && calculatedDays > 0 && !policy.allowsNegativeBalance && remaining < calculatedDays;

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit() {
    if (!form.typeId) { toast.error('Select a leave type'); return; }
    if (!form.startDate || !form.endDate) { toast.error('Select start and end dates'); return; }
    if (form.startDate > form.endDate) { toast.error('Start date must be on or before end date'); return; }
    if (calculatedDays <= 0) { toast.error('No working days in selected range'); return; }

    setSubmitting(true);
    try {
      const payload = {
        typeId: form.typeId,
        startDate: form.startDate,
        endDate: form.endDate,
        reason: form.reason || undefined,
      };
      if (editId) {
        await employeeApi.updateRequest(editId, payload);
        toast.success('Leave request updated — sent back for approval');
        navigate(`/my-requests/${editId}`);
      } else {
        const res = await employeeApi.submitRequest(payload);
        const status = res.request?.status;
        toast.success(status === 'approved' ? 'Request auto-approved!' : 'Request submitted for approval');
        navigate('/my-requests');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        <span className="ml-3 text-sm">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="mb-6 text-2xl font-bold tracking-tight">{editId ? 'Edit Leave Request' : 'Request Leave'}</h1>

      <Card>
        <CardContent className="flex flex-col gap-4 pt-6">
          <Select
            label="Leave type"
            value={form.typeId}
            onChange={(e) => set('typeId', e.target.value)}
            options={leaveTypes.map((t) => ({ value: t.rowKey, label: t.displayName }))}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Start date"
              type="date"
              value={form.startDate}
              onChange={(e) => set('startDate', e.target.value)}
            />
            <Input
              label="End date"
              type="date"
              value={form.endDate}
              onChange={(e) => set('endDate', e.target.value)}
            />
          </div>

          {calculatedDays !== null && (
            <div className="rounded-md border border-input bg-muted/50 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Working days</span>
                <span className={`text-xl font-bold ${calculatedDays > 0 ? 'text-foreground' : 'text-profun-pink'}`}>
                  {formatDays(calculatedDays)} day{calculatedDays !== 1 ? 's' : ''}
                </span>
              </div>
              {balance && (
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>Current balance: {formatDays(remaining)} days remaining</span>
                  {insufficientBalance && (
                    <span className="font-semibold text-profun-pink">Insufficient balance</span>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-muted-foreground">
              Reason {policy.requiresApproval ? '' : '(optional)'}
            </label>
            <textarea
              value={form.reason}
              onChange={(e) => set('reason', e.target.value)}
              rows={3}
              placeholder="Why are you taking this leave?"
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ resize: 'vertical' }}
            />
          </div>

          {selectedType && (
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              {policy.requiresApproval && <span>Requires approval</span>}
              {policy.minNoticeDays > 0 && <span>Min {policy.minNoticeDays} days notice</span>}
              {policy.maxConsecutiveDays && <span>Max {policy.maxConsecutiveDays} consecutive days</span>}
            </div>
          )}

          <div className="pt-2">
            <Button onClick={handleSubmit} loading={submitting} disabled={insufficientBalance}>
              {editId ? 'Save changes' : 'Submit request'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
